import { withTenderTime } from "../../../context/TenderTimeProvider";
import serverClock from "../../../services/serverClock";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "../order-info/OrderInfoAviation.module.scss";
import { Aviation, File } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import Loading from "../../shared/Loading/Loading";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";
import OrderInfoAviationStepper from "../order-info/components/desktop/OrderInfoAviationStepper";
import AviationCargoTab from "../order-info/components/desktop/tabs/AviationCargoTab";
import AviationDocumentsTab from "../order-info/components/desktop/tabs/AviationDocumentsTab";
import OrderInfoAviationExpeditorHeader from "./components/OrderInfoAviationExpeditorHeader";
import OrderInfoAviationExpeditorTabs from "./components/OrderInfoAviationExpeditorTabs";
import AviationChangeAmountModal from "./components/AviationChangeAmountModal";
import AviationExpeditorActionsTab from "./components/tabs/AviationExpeditorActionsTab";
import AviationExpeditorHistoryTab from "./components/tabs/AviationExpeditorHistoryTab";
import { useAirOrderRealtimeEvents } from "../../../hooks/useAirOrderRealtimeEvents";
import {
  AIR_ORDER_DETAIL_REFRESH_TARGETS,
  CHAT_REFRESH_TARGETS,
} from "../../../services/orderRealtimeEvents";
import { useChatUnreadRefresh } from "../../../hooks/useChatUnreadRefresh";
import { useAviationTenderState } from "./utils/useAviationTenderState";

const normalizeOrderDates = (sourceOrder) => {
  const order = { ...sourceOrder };

  if (order.shippingDate) {
    const [date] = order.shippingDate.split("T");
    const timeStart = String(order.shippingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.shippingHourTo ?? 0).padStart(2, "0");
    order.shippingDate = `${date}T${timeStart}:00:00`;
    order.shippingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  if (order.unloadingDate) {
    const [date] = order.unloadingDate.split("T");
    const timeStart = String(order.unloadingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.unloadingHourTo ?? 0).padStart(2, "0");
    order.unloadingDate = `${date}T${timeStart}:00:00`;
    order.unloadingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  return order;
};

const normalizeExtraCost = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const OrderInfoAviationExpeditor = ({
  isOpen,
  setIsOpen,
  orderId,
  onForceUpdate,
  tenderOrderInfo,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const userCompanyId = useSelector((state) => state.user.companyId ?? state.user.company?.id);
  const orderRequestIdRef = useRef(0);
  const documentsRequestIdRef = useRef(0);
  const extraCostRequestIdRef = useRef(0);
  const historyRequestIdRef = useRef(0);
  const wasOpenRef = useRef(false);
  const rejectedTenderIdsRef = useRef(new Set());
  const translationRef = useRef(t);
  translationRef.current = t;

  const [order, setOrder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [extraCost, setExtraCost] = useState(null);
  const [history, setHistory] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [activeTab, setActiveTab] = useState("actions");
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChangeAmountOpen, setIsChangeAmountOpen] = useState(false);
  const tenderState = useAviationTenderState({ order, orderId, isOpen, isLoading, userCompanyId });
  const { view } = tenderState;
  const {
    refreshUnreadMessages,
    refreshUnreadOnClose,
    scheduleUnreadRefresh,
  } = useChatUnreadRefresh({
    getUnreadMessages: Aviation.getUnreadMessages,
    orderId,
    unreadMessagesCount,
    setUnreadMessagesCount,
  });

  const tryRejectExpiredTender = useCallback(
    async (currentOrder) => {
      const status = String(currentOrder?.status || "").toLowerCase();
      const tenderEnd = currentOrder?.tenderDateEnd;
      const orderIdToReject = currentOrder?.id;

      if (
        currentOrder?.assignmentMethod === "DirectAssignment" ||
        status !== "awaitingoffer" ||
        !orderIdToReject ||
        !tenderEnd ||
        rejectedTenderIdsRef.current.has(orderIdToReject)
      ) {
        return;
      }

      try {
        if (serverClock.getNowMs() == null) await serverClock.sync();
        const nowMs = serverClock.getNowMs();
        if (nowMs == null || new Date(tenderEnd) >= nowMs) return;
        const response = await Aviation.getSuggestedPriceByExpeditor(orderIdToReject);
        if (!Array.isArray(response?.data) || response.data.length > 0) return;

        const currentNowMs = serverClock.getNowMs();
        if (currentNowMs == null || new Date(tenderEnd) >= currentNowMs) return;
        await Aviation.rejectTender(orderIdToReject);
        rejectedTenderIdsRef.current.add(orderIdToReject);
      } catch (error) {
        console.error(error);
      }
    },
    [],
  );

  const fetchOrderData = useCallback(async ({ silent = false } = {}) => {
    if (!orderId || !isOpen) return null;

    const requestId = ++orderRequestIdRef.current;
    if (!silent) {
      setIsLoading(true);
    }

    try {
      const response = await Aviation.getAirOrdersById(orderId, currentLanguage);
      if (requestId !== orderRequestIdRef.current) return null;

      const nextOrder = normalizeOrderDates({
        ...response.data,
        expeditorName: tenderOrderInfo?.expeditorName || response.data?.expeditorName,
      });
      setOrder(nextOrder);
      return nextOrder;
    } catch (error) {
      if (requestId === orderRequestIdRef.current) {
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      }
      return null;
    } finally {
      if (requestId === orderRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentLanguage, isOpen, orderId, tenderOrderInfo?.expeditorName]);

  const fetchDocuments = useCallback(async (currentOrderId) => {
    if (!currentOrderId) return;

    const requestId = ++documentsRequestIdRef.current;

    try {
      const [typeSixResponse, typeSevenResponse] = await Promise.all([
        File.getFilesByOrder(currentOrderId, 6),
        File.getFilesByOrder(currentOrderId, 7),
      ]);
      if (requestId === documentsRequestIdRef.current) {
        setDocuments(
          [...(typeSixResponse.data || []), ...(typeSevenResponse.data || [])].reverse(),
        );
      }
    } catch (error) {
      if (requestId === documentsRequestIdRef.current) {
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      }
    }
  }, []);

  const fetchExtraCost = useCallback(async (currentOrderId) => {
    if (!currentOrderId) return;

    const requestId = ++extraCostRequestIdRef.current;

    try {
      const response = await Aviation.getExtraCostsByOrderId(currentOrderId);
      if (requestId === extraCostRequestIdRef.current) {
        setExtraCost(normalizeExtraCost(response.data));
      }
    } catch (error) {
      if (requestId === extraCostRequestIdRef.current) {
        console.error(error);
        setExtraCost(null);
      }
    }
  }, []);

  const fetchHistory = useCallback(async (currentOrderId) => {
    if (!currentOrderId) return;

    const requestId = ++historyRequestIdRef.current;

    try {
      const response = await Aviation.getHistory(currentOrderId);
      if (requestId === historyRequestIdRef.current) {
        setHistory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      if (requestId === historyRequestIdRef.current) {
        console.error(error);
        setHistory([]);
      }
    }
  }, []);

  useAirOrderRealtimeEvents(({ target, data }) => {
    const isMatchingOrder =
      data?.orderId != null && String(data.orderId) === String(orderId);

    if (!isOpen || !isMatchingOrder) return;

    if (CHAT_REFRESH_TARGETS.has(target)) {
      refreshUnreadMessages();
      return;
    }

    if (AIR_ORDER_DETAIL_REFRESH_TARGETS.has(target)) {
      fetchOrderData({ silent: true });
      fetchDocuments(orderId);
      fetchExtraCost(orderId);
      fetchHistory(orderId);
    }
  });

  useEffect(() => {
    orderRequestIdRef.current += 1;
    documentsRequestIdRef.current += 1;
    extraCostRequestIdRef.current += 1;
    historyRequestIdRef.current += 1;
    setOrder(null);
    setDocuments([]);
    setExtraCost(null);
    setHistory([]);
    setUnreadMessagesCount(0);
    setActiveTab("actions");
    setIsChatOpen(false);
    setIsChangeAmountOpen(false);
    fetchOrderData();

    return () => {
      orderRequestIdRef.current += 1;
      documentsRequestIdRef.current += 1;
      extraCostRequestIdRef.current += 1;
      historyRequestIdRef.current += 1;
    };
  }, [fetchOrderData]);

  useEffect(() => {
    if (!order?.id) return undefined;

    let isActive = true;
    fetchDocuments(order.id);
    fetchExtraCost(order.id);
    fetchHistory(order.id);
    Aviation.getUnreadMessages(order.id)
      .then((response) => {
        if (isActive && response.status === 200) {
          setUnreadMessagesCount(response.data?.count || 0);
        }
      })
      .catch((error) => console.error(error));

    return () => {
      isActive = false;
    };
  }, [fetchDocuments, fetchExtraCost, fetchHistory, order?.id]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      tryRejectExpiredTender(order);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, order, tryRejectExpiredTender]);

  const handleAmountSuccess = () => {
    fetchOrderData();
    fetchExtraCost(order?.id);
    onForceUpdate?.();
  };

  const handleOpenChat = () => {
    setIsChatOpen(true);
    scheduleUnreadRefresh();
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    refreshUnreadOnClose();
  };

  return (
    <>
      <div
        className={cn(style.overlay, { [style.overlay_open]: isOpen })}
        onClick={() => setIsOpen?.(false)}
      />
      <div className={cn(style.wrapper, { [style.open]: isOpen })}>
        <OrderInfoAviationExpeditorHeader
          item={order}
          setIsOpen={setIsOpen}
          onOpenChat={handleOpenChat}
          unreadCount={unreadMessagesCount}
          onChangeAmount={() => setIsChangeAmountOpen(true)}
          isSummaryOnly={view !== "details"}
        />

        {isLoading || view === "loading" ? <Loading borderRadius="0" /> : null}

        {view === "details" ? (
          <>
            <OrderInfoAviationStepper status={order.status} assignmentMethod={order.assignmentMethod} />
            <OrderInfoAviationExpeditorTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              documentsCount={documents.length}
            />
            <div className={style.content} data-testid="aviation-expeditor-tab-content">
              {activeTab === "actions" ? (
                <AviationExpeditorActionsTab
                  order={order}
                  tenderState={tenderState}
                  history={history}
                  fetchOrderData={fetchOrderData}
                  onForceUpdate={onForceUpdate}
                  refreshDocuments={() => fetchDocuments(order.id)}
                  onClose={() => setIsOpen?.(false)}
                />
              ) : null}
              {activeTab === "cargo" ? <AviationCargoTab order={order} /> : null}
              {activeTab === "documents" ? (
                <AviationDocumentsTab
                  order={order}
                  documents={documents}
                  refreshDocuments={() => fetchDocuments(order.id)}
                />
              ) : null}
              {activeTab === "history" ? (
                <AviationExpeditorHistoryTab history={history} order={order} />
              ) : null}
            </div>
          </>
        ) : null}
        {view === "tender" ? (
          <div className={style.content}>
            <AviationExpeditorActionsTab
              order={order}
              tenderState={tenderState}
              history={history}
              onForceUpdate={onForceUpdate}
              onClose={() => setIsOpen?.(false)}
            />
          </div>
        ) : null}
        {tenderState.showBidsError ? (
          <div className={style.content} role="alert">
            {t("orderInfo.genericError")}
          </div>
        ) : null}
      </div>

      {isChatOpen && view === "details" ? (
        <OrdersChatHub
          isOpen={isChatOpen}
          orderData={order}
          orderId={order.id}
          orderNumber={order.number}
          onClose={handleCloseChat}
          serviceType="aviation"
        />
      ) : null}

      <AviationChangeAmountModal
        onRefreshOrder={fetchOrderData}
        isOpen={isChangeAmountOpen && view === "details"}
        order={order}
        extraCost={extraCost}
        onClose={() => setIsChangeAmountOpen(false)}
        onSuccess={handleAmountSuccess}
      />
    </>
  );
};

export default withTenderTime(OrderInfoAviationExpeditor);
